import { useState, useEffect, useRef } from 'react';
import { useProducts } from '@/hooks/useProducts';
import { ProductGrid } from '@/components/products/ProductGrid';
import { CategoryFilter } from '@/components/products/CategoryFilter';
import { ProductGridSkeleton } from '@/components/ui/SkeletonLoader';
import { trackViewItemList } from '@/services/analytics';

const CATEGORIES = ['Enclosures', 'Gadgets', 'Tools', 'Decorative', 'Mechanical'] as const;

export const CatalogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data, isLoading, error } = useProducts({
    category: selectedCategory ?? undefined,
  });
  const lastTrackedCategory = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!data?.data.length) return;
    // Only fire once per category change (not on re-renders)
    if (lastTrackedCategory.current === selectedCategory) return;
    lastTrackedCategory.current = selectedCategory;

    trackViewItemList({
      item_list_name: selectedCategory ?? 'all_products',
      items: data.data.slice(0, 10).map((p) => ({
        item_id: p.productId,
        item_name: p.name,
        item_category: p.category,
        price: p.basePrice,
      })),
    });
  }, [data, selectedCategory]);

  return (
    <div
      className="container-main"
      style={{
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <h1
          style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Product Catalog
        </h1>
        <p
          style={{
            marginTop: 'var(--space-2)',
            color: 'var(--text-secondary)',
          }}
        >
          Browse our collection of ready-to-print items
        </p>
      </div>

      <div style={{ marginBottom: 'var(--space-8)' }}>
        <CategoryFilter
          categories={[...CATEGORIES]}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
        />
      </div>

      {isLoading && <ProductGridSkeleton />}

      {error && (
        <div
          style={{
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-error-50)',
            padding: 'var(--space-4)',
            color: 'var(--color-error-700)',
          }}
        >
          Failed to load products. Please try again.
        </div>
      )}

      {data && <ProductGrid products={data.data} />}
    </div>
  );
};
